import { useState } from "react";
import axiosInstance from "config/axiosInstance";
import { useAppAlert } from "hooks/useAppAlert";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { TextField, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { toPeruDateInputValue, toPeruFirstDayOfMonth } from "utils/datePeru";
import { IoDocumentText, IoGrid } from "react-icons/io5";

export default function ExportReports() {
  const { showAlert, AlertBanner } = useAppAlert();
  const [fechaInicio, setFechaInicio] = useState(() => toPeruFirstDayOfMonth(new Date()));
  const [fechaFin, setFechaFin] = useState(() => toPeruDateInputValue(new Date()));
  const [loading, setLoading] = useState(false);
  const [formatoExport, setFormatoExport] = useState("simple"); // simple o detallado
  const [tipoArchivo, setTipoArchivo] = useState("excel"); // excel o pdf

  const handleExport = async () => {
    if (!fechaInicio || !fechaFin) {
      showAlert("Selecciona fecha de inicio y fin", "warning");
      return;
    }
    setLoading(true);
    try {
      // Verify session first
      try {
        const me = await axiosInstance.get("/auth/me");
        if (!me.data || !me.data.success) {
          showAlert(me.data?.message || "No autenticado. Inicia sesión de nuevo.", "error");
          window.location.href = "/authentication/sign-in";
          return;
        }
      } catch (e) {
        showAlert("No autenticado. Inicia sesión de nuevo.", "error");
        window.location.href = "/authentication/sign-in";
        return;
      }

      const res = await axiosInstance.get("/reports/depositos", {
        params: { 
          fechaInicio, 
          fechaFin,
          formato: formatoExport,
          tipo: tipoArchivo
        },
        responseType: "blob",
      });
      const contentType = (res.headers && res.headers["content-type"]) || "";
      // If server returned JSON (error), read it and show message
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        try {
          const obj = JSON.parse(text);
          showAlert(obj.message || "Error al generar el reporte", "error");
        } catch (e) {
          showAlert("Error al generar el reporte", "error");
        }
        return;
      }

      // Use the correct content type from server
      const blob = new Blob([res.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cd = res.headers["content-disposition"] || "";
      let filename = tipoArchivo === 'pdf' ? "reporte.pdf" : "reporte.xlsx";
      const m = cd.match(/filename="?([^";]+)"?/);
      if (m) filename = m[1];
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Try to surface server error message if available
      if (err.response && err.response.data) {
        try {
          const text = await err.response.data.text();
          const obj = JSON.parse(text);
          showAlert(obj.message || "Error al generar el reporte", "error");
          setLoading(false);
          return;
        } catch (e) {
          // fallthrough
        }
      }
      showAlert("Error al generar el reporte", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VuiBox 
      mt={3} 
      p={3} 
      sx={{ 
        backgroundColor: "rgba(255,255,255,0.05)", 
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.1)"
      }}
    >
      <AlertBanner />
      <VuiBox display="flex" alignItems="center" gap={1} mb={2}>
        <IoDocumentText size="24px" color="#0075FF" />
        <VuiTypography variant="h5" color="white" fontWeight="bold">
          Exportar Reportes
        </VuiTypography>
      </VuiBox>

      <VuiBox display="flex" gap={2} flexDirection="column">
        {/* Selector de fechas */}
        <VuiBox display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
          <VuiBox sx={{ width: "200px" }}>
            <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
              Fecha inicio
            </VuiTypography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "10px",
                  height: "45px",
                  cursor: "pointer",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                },
                "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 200, 83, 0.6)",
                },
                "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00c853",
                  borderWidth: "2px",
                },
                "& input[type='date']::-webkit-calendar-picker-indicator": {
                  filter: "invert(1) brightness(1.5)",
                  cursor: "pointer",
                  width: "20px",
                  height: "20px",
                  marginLeft: "4px",
                },
                "& input[type='date']": {
                  colorScheme: "dark",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                },
                "& .MuiInputBase-input": {
                  cursor: "pointer",
                  padding: "10px 12px",
                },
              }}
            />
          </VuiBox>

          <VuiBox sx={{ width: "200px" }}>
            <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
              Fecha fin
            </VuiTypography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "10px",
                  height: "45px",
                  cursor: "pointer",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                },
                "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 200, 83, 0.6)",
                },
                "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00c853",
                  borderWidth: "2px",
                },
                "& input[type='date']::-webkit-calendar-picker-indicator": {
                  filter: "invert(1) brightness(1.5)",
                  cursor: "pointer",
                  width: "20px",
                  height: "20px",
                  marginLeft: "4px",
                },
                "& input[type='date']": {
                  colorScheme: "dark",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                },
                "& .MuiInputBase-input": {
                  cursor: "pointer",
                  padding: "10px 12px",
                },
              }}
            />
          </VuiBox>
        </VuiBox>

        {/* Selectores de formato y tipo */}
        <VuiBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <VuiBox flex={1} minWidth="200px">
            <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
              Tipo de archivo
            </VuiTypography>
            <FormControl fullWidth size="small">
              <Select
                value={tipoArchivo}
                onChange={(e) => setTipoArchivo(e.target.value)}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "10px",
                  height: "45px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
              >
                <MenuItem value="excel">📊 Excel (.xlsx)</MenuItem>
                <MenuItem value="pdf">📄 PDF</MenuItem>
              </Select>
            </FormControl>
          </VuiBox>

          <VuiBox flex={1} minWidth="200px">
            <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
              Formato del reporte
            </VuiTypography>
            <FormControl fullWidth size="small">
              <Select
                value={formatoExport}
                onChange={(e) => setFormatoExport(e.target.value)}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "10px",
                  height: "45px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
              >
                <MenuItem value="simple">📋 Simple (Básico)</MenuItem>
                <MenuItem value="detallado">📊 Detallado (Con totales y gráficos)</MenuItem>
              </Select>
            </FormControl>
          </VuiBox>
        </VuiBox>

        {/* Descripción del formato seleccionado */}
        <VuiBox 
          p={2} 
          sx={{ 
            backgroundColor: "rgba(0, 117, 255, 0.1)",
            borderRadius: "10px",
            border: "1px solid rgba(0, 117, 255, 0.3)"
          }}
        >
          <VuiTypography variant="caption" color="info" fontWeight="medium">
            {formatoExport === "simple" ? (
              "📋 Formato Simple: Incluye fecha, nombre, banco y monto."
            ) : (
              "📊 Formato Detallado: Incluye logo, totales por banco, gráficos, resumen ejecutivo y estadísticas."
            )}
          </VuiTypography>
        </VuiBox>

        {/* Botón de exportar */}
        <VuiButton
          color="info"
          onClick={handleExport}
          disabled={loading}
          sx={{
            height: "50px",
            fontSize: "1rem",
            fontWeight: "bold",
            background: "linear-gradient(135deg, #0075FF 0%, #0056CC 100%)",
            boxShadow: "0 4px 12px rgba(0, 117, 255, 0.4)",
            "&:hover": {
              boxShadow: "0 6px 16px rgba(0, 117, 255, 0.6)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <IoGrid size="20px" style={{ marginRight: "10px" }} />
          {loading ? "Generando..." : `Generar ${tipoArchivo === "excel" ? "Excel" : "PDF"}`}
        </VuiButton>
      </VuiBox>
    </VuiBox>
  );
}
